/**
 * One learner: why they are or are not being asked anything, what has gone out, and
 * what they said back. F-81, F-83, F-84.
 *
 * English-only, no `t()`. See the note at the top of `Console.tsx`.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getLearner,
  isError,
  listMessages,
  listSends,
  nextCursorOf,
  revealPhone,
  sweep,
  type AdminLearner,
  type AdminLearnerDetail,
  type AdminSweepState,
  type ApiError,
} from './api';
import {
  ConfirmedAction,
  Failed,
  Id,
  Nil,
  Waiting,
  When,
  abbreviate,
  ago,
  inZone,
  localTitle,
  stamp,
  usePages,
} from './parts';
import { LOG_GROUPS, logsAround, logsRecent } from './logs';
import { DisputesPanel } from './Disputes';

/**
 * How long silence is allowed to last before it is a fault rather than a wait.
 *
 * The sweep runs every five minutes, so three empty sweeps is the point at which
 * "about to send" stops being a plausible explanation. Anything shorter would cry
 * wolf between two ordinary sweeps.
 */
const SILENCE_IS_SUSPICIOUS_MS = 15 * 60_000;

/**
 * The six token counters as one line, tolerating an absent `usage`.
 *
 * Summed rather than itemised, because the counters are per **account** and this
 * screen is one learner: one line saying so is honest, where six columns would imply
 * an attribution the counters do not carry.
 */
function summariseUsage(usage: AdminLearner['usage'] | undefined): string {
  if (!usage) return 'no counters';
  const tokens =
    (usage.inputTokens ?? 0) +
    (usage.outputTokens ?? 0) +
    (usage.cacheReadTokens ?? 0) +
    (usage.cacheWriteTokens ?? 0) +
    (usage.batchInputTokens ?? 0) +
    (usage.batchOutputTokens ?? 0);
  return `${abbreviate(tokens)} tokens · ${usage.calls ?? 0} calls · ${usage.webSearches ?? 0} searches`;
}

type Tone = 'expected' | 'fault' | 'unknown';

interface Verdict {
  tone: Tone;
  headline: string;
  detail: string;
}

/**
 * Which of the legitimate reasons for silence applies — or none, which is the answer
 * worth shouting about.
 *
 * **This panel exists because "correctly quiet" and "broken" are indistinguishable
 * from outside.** Sends happen whenever a five-minute sweep finds due work inside the
 * learner's reach window, so from a phone the timing looks arbitrary: an hour with no
 * question could be quiet hours, a spent daily allowance, an unanswered question
 * still standing, a paused learner, or a sweep that has been failing since Tuesday.
 * Every one of those was previously answered by reading the sweep's source with the
 * AWS CLI, which is the diagnosis M8 exists to stop doing.
 *
 * The gate is read from `reason`, the sweep's own word for where its decision stopped,
 * and **not** from `insideWindow` — that flag is true only when a send would happen
 * now, so it also reads false when the clock is fine and the daily cap is spent.
 * Deriving "outside their hours" from it would blame the wrong gate at noon.
 *
 * An open question is the *ordinary* state and is written as one. The system sends at
 * most one question at a time and will not send another until it is answered, so most
 * of the time the honest answer here is "it is waiting for them", and phrasing that
 * as a problem would train the operator to ignore the panel.
 */
function diagnose(
  learner: AdminLearner,
  state: AdminSweepState | undefined,
  now = Date.now(),
): Verdict {
  if (!state) {
    return {
      tone: 'unknown',
      headline: 'The API did not return a sweep state',
      detail:
        'This panel needs `sweepState` on /api/admin/learner. Everything below is still current.',
    };
  }

  if (state.paused) {
    return {
      tone: 'expected',
      headline: 'Sending is paused',
      detail: 'Nothing will go out until the learner is unpaused. This is not a fault.',
    };
  }

  const objective = learner.objective;
  if (!objective) {
    return {
      tone: 'expected',
      headline: 'No objective',
      detail: 'There is nothing to ask about.',
    };
  }
  if (objective.status !== 'active') {
    return {
      tone: 'expected',
      headline: `The objective is ${objective.status}, not active`,
      detail:
        'Review states are only written when Start is pressed, so nothing is due and nothing can be sent.',
    };
  }

  if (state.openItemId) {
    return {
      tone: 'expected',
      headline: `A question is waiting for an answer, sent ${ago(state.openSince, now)}`,
      detail: `One question at a time: nothing else goes out until this one is answered. ${state.reminders} reminder${state.reminders === 1 ? '' : 's'} sent about it.`,
    };
  }

  /*
   * The two refusals, which are faults even though they are also "reasons".
   *
   * The sweep declines to send at all rather than assume a schedule nobody agreed to,
   * so these are not a wait: nothing will ever arrive until somebody fixes the row.
   * Reading them as ordinary silence is how a learner sits in a working system for a
   * fortnight receiving nothing.
   */
  if (state.reason === 'no_timezone') {
    return {
      tone: 'fault',
      headline: 'No usable timezone, so the sweep refuses to send',
      detail: `The learner row holds ${learner.timezone || 'nothing'}, and reachable hours cannot be interpreted without a zone. Nothing will be sent until it is fixed.`,
    };
  }
  if (state.reason === 'no_window') {
    return {
      tone: 'fault',
      headline: 'No reach window saved, so the sweep refuses to send',
      detail:
        'Sending needs hours the learner agreed to. Nothing goes out until a window is saved on their schedule screen.',
    };
  }

  if (state.reason === 'before_window' || state.reason === 'after_window') {
    return {
      tone: 'expected',
      headline: `Outside their hours — ${state.localTimeNow ?? 'unknown'} local, window ${state.reachFrom ?? '?'}–${state.reachUntil ?? '?'}`,
      detail: `Their zone is ${state.timezone ?? 'unknown'}. Next send ${state.nextSendAt ? `${ago(state.nextSendAt, now)}, at ${inZone(state.nextSendAt, state.timezone ?? undefined)} their time` : 'not computable'}.`,
    };
  }

  if (state.reason === 'limit_reached' || state.remainingToday <= 0) {
    return {
      tone: 'expected',
      headline: `Today's questions are spent — ${state.sentToday} of ${state.dailyLimit ?? '?'}`,
      detail: `The next send is on their next local day${state.nextSendAt ? `, ${ago(state.nextSendAt, now)}` : ''}, not at UTC midnight.`,
    };
  }

  /*
   * Nothing is in the way. Which leaves two possibilities, and the panel must not
   * pretend to know which: either nothing is due yet — perfectly healthy — or the
   * sweep is not delivering.
   *
   * It says both, because claiming a fault the operator then disproves is how a
   * diagnostic loses its authority, and hiding a real one is how a broken sweep goes
   * unnoticed for a week. The item list sorted by due date settles it, and the fault
   * line links there.
   */
  const last = learner.lastOutboundAt ? Date.parse(learner.lastOutboundAt) : NaN;
  const quietFor = Number.isNaN(last) ? Infinity : now - last;
  if (quietFor > SILENCE_IS_SUSPICIOUS_MS) {
    return {
      tone: 'fault',
      headline: learner.lastOutboundAt
        ? `Nothing is stopping a send, and nothing has gone out for ${ago(learner.lastOutboundAt, now)}`
        : 'Nothing is stopping a send, and nothing has ever gone out',
      detail:
        'Inside their hours, no question open, allowance left, not paused. If anything is due, a sweep should have sent it within five minutes — check whether anything is actually due before blaming the sweep.',
    };
  }

  return {
    tone: 'expected',
    headline: 'Clear to send',
    detail: `Inside their hours, ${state.remainingToday} of ${state.dailyLimit ?? '?'} questions left today, nothing open. Last send ${ago(learner.lastOutboundAt, now)}.`,
  };
}

/** One condition, with its own answer. The verdict above says which one won. */
function Check({ label, blocking, value }: { label: string; blocking: boolean; value: string }) {
  return (
    <div className={blocking ? 'check is-blocking' : 'check'}>
      <span className="check-label">{label}</span>
      <span className="check-value">{value}</span>
    </div>
  );
}

function WhyNothing({
  learner,
  state,
}: {
  learner: AdminLearner;
  state: AdminSweepState | undefined;
}) {
  const verdict = diagnose(learner, state);
  const itemsHref = learner.objective
    ? `/admin/objective/${learner.objective.objectiveId}/items?sort=due`
    : undefined;
  const clockBlocked = state?.reason === 'before_window' || state?.reason === 'after_window';

  return (
    <section className={`admin-panel verdict-${verdict.tone}`}>
      <header className="admin-head">
        <h2>Why nothing is arriving</h2>
        {state && <p className="sub">sweep says: {state.reason}</p>}
      </header>
      <p className="verdict-headline">{verdict.headline}</p>
      <p className="sub">
        {verdict.detail}
        {verdict.tone === 'fault' && itemsHref && (
          <>
            {' '}
            <Link className="admin-link" to={itemsHref}>
              items by due date →
            </Link>
          </>
        )}
      </p>

      {state && (
        <div className="checks">
          <Check label="paused" blocking={state.paused} value={state.paused ? 'yes' : 'no'} />
          <Check
            label="question open"
            blocking={!!state.openItemId}
            value={state.openItemId ? `yes, sent ${ago(state.openSince)}` : 'no'}
          />
          <Check
            label="inside hours"
            blocking={clockBlocked}
            value={
              state.reachFrom
                ? `${clockBlocked ? 'no' : 'yes'} · ${state.localTimeNow ?? '?'} local, ${state.reachFrom}–${state.reachUntil} ${state.timezone ?? ''}`
                : 'no window saved'
            }
          />
          <Check
            label="allowance"
            blocking={state.reason === 'limit_reached' || state.remainingToday <= 0}
            value={`${state.sentToday} of ${state.dailyLimit ?? '?'} questions asked, ${state.remainingToday} left`}
          />
        </div>
      )}
    </section>
  );
}

/**
 * Every question that went out, newest first.
 *
 * A timeline rather than a count, because the question being asked of it is "should
 * that have been sent then" — which needs the clock in the learner's own zone beside
 * the raw timestamp, and the due date the sweep saw beside the one it wrote after.
 *
 * An unanswered send is the interesting row and is coloured as one. It is not
 * necessarily a fault — the newest one is usually the open question, and the learner
 * may be asleep — but it is the row every "nabu went quiet" complaint resolves to, so
 * it must not read like the answered ones.
 *
 * **What this cannot show, it says rather than implies.** Grading clears `sentAt`, and
 * there is one review row per item, so an answered question's exact send time is gone
 * and an item asked three times appears once. Those rows are ordered by their answer
 * and labelled as such; the per-day counters underneath are the durable record of how
 * much went out on a given day.
 */
function Sends({ userId, timezone }: { userId: string; timezone: string | undefined }) {
  const [days, setDays] = useState<{ day: string; sent: number }[]>([]);

  const fetchPage = useCallback(
    async (cursor?: string) => {
      const result = await listSends(userId, cursor);
      if (isError(result)) return result;
      // Sent with every page and identical each time, so the last one in wins — which
      // avoids accumulating sixty duplicated days across four pages.
      if (result.days) setDays(result.days);
      return { rows: result.sends, next: nextCursorOf(result) };
    },
    [userId],
  );
  const { rows, error, loading, done, loadMore, loadAll } = usePages(fetchPage);

  const unanswered = rows.filter((send) => !send.answeredAt).length;

  return (
    <section className="admin-panel">
      <header className="admin-head">
        <h2>Sent questions</h2>
        <p className="sub">
          {rows.length} loaded{done ? ', all of them' : ''} · {unanswered} unanswered
        </p>
      </header>

      {error && <Failed error={error} what="Send timeline" />}
      {!error && rows.length === 0 && !loading && (
        <p className="admin-empty">Nothing has ever been sent to this learner.</p>
      )}

      {days.length > 0 && (
        <>
          <p className="sub">
            Messages per local day, from the counters the sweep increments — exact even where a
            send time below has been overwritten by grading.
          </p>
          <div className="days">
            {days.map((entry) => (
              <span key={entry.day} className="day">
                <span className="day-label">{entry.day}</span>
                <span className="day-count">{entry.sent}</span>
              </span>
            ))}
          </div>
        </>
      )}

      {rows.length > 0 && (
        <div className="admin-scroll">
          <table className="admin-table timeline">
            <thead>
              <tr>
                <th>When</th>
                <th>Answered</th>
                <th>Question</th>
                <th>Due when sent</th>
                <th>Due now</th>
                <th>Item</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((send) => {
                const logs = send.sentAt ? logsAround(LOG_GROUPS.sweep, send.sentAt) : undefined;
                return (
                  <tr
                    key={`${send.itemId}-${send.at}`}
                    className={send.answeredAt ? undefined : 'is-unanswered'}
                  >
                    <td>
                      <When iso={send.sentAt ?? send.at} timezone={timezone} />
                      {send.sentAt ? (
                        logs && (
                          <a
                            className="admin-link sub"
                            href={logs}
                            target="_blank"
                            rel="noreferrer"
                          >
                            sweep log ±10m
                          </a>
                        )
                      ) : (
                        /* Ordered by its answer, because grading cleared the send
                           time. Said out loud: a timestamp that silently means
                           something else on some rows is worse than a row that admits
                           what it is. */
                        <span className="sub">answer time · send time not retained</span>
                      )}
                    </td>
                    <td>
                      {send.answeredAt ? (
                        <span className="stack">
                          <span className={`tag outcome-${send.outcome ?? 'none'}`}>
                            {send.outcome ?? 'ungraded'}
                          </span>
                          <span className="sub">{stamp(send.answeredAt)}</span>
                        </span>
                      ) : (
                        <span className="stack">
                          <span className="tag warn">unanswered</span>
                          <span className="sub">
                            {send.reminders === null
                              ? 'reminders unknown'
                              : `${send.reminders} reminder${send.reminders === 1 ? '' : 's'}`}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="cell-prose">
                      <span className={`tag kind-${send.kind}`}>{send.kind}</span>{' '}
                      <span className="wrapped">{send.prompt || '(prompt not found)'}</span>
                      {send.promptTruncated && <span className="sub"> …truncated</span>}
                    </td>
                    <td className="num" title={send.dueAtSend ?? undefined}>
                      {send.dueAtSend ? inZone(send.dueAtSend, timezone) : <Nil />}
                    </td>
                    <td className="num" title={send.due}>
                      {send.due ? ago(send.due) : <Nil />}
                    </td>
                    <td>
                      <span className="stack">
                        <Id value={send.itemId} />
                        <span className="sub">
                          {send.state} · {send.reps} reps · {send.lapses} lapses
                        </span>
                        <Link
                          className="admin-link sub"
                          to={`/admin/objective/${send.objectiveId}/items?node=${encodeURIComponent(send.nodeId)}`}
                        >
                          node →
                        </Link>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-actions">
        {loading && <span className="sub">loading…</span>}
        {!done && !loading && (
          <>
            <button type="button" className="admin-button quiet" onClick={loadMore}>
              Load more
            </button>
            <button type="button" className="admin-button quiet" onClick={loadAll}>
              Load all
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/** Both sides of the conversation, in order. F-84. */
function Messages({ userId, timezone }: { userId: string; timezone: string | undefined }) {
  const fetchPage = useCallback(
    async (cursor?: string) => {
      const result = await listMessages(userId, cursor);
      if (isError(result)) return result;
      return { rows: result.messages, next: nextCursorOf(result) };
    },
    [userId],
  );
  const { rows, error, loading, done, loadMore, loadAll } = usePages(fetchPage);

  return (
    <section className="admin-panel">
      <header className="admin-head">
        <h2>Messages</h2>
        <p className="sub">
          {rows.length} loaded{done ? ', all of them' : ''} · newest first
        </p>
      </header>

      {error && <Failed error={error} what="Message history" />}
      {!error && rows.length === 0 && !loading && (
        <p className="admin-empty">
          No messages logged. The sweep's outgoing questions are not written to this log — the
          send timeline above is where those are.
        </p>
      )}

      {rows.length > 0 && (
        <div className="admin-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Dir</th>
                <th>Text</th>
                <th>About</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((message) => (
                <tr key={`${message.at}-${message.messageId}`}>
                  <td>
                    <When iso={message.at} timezone={timezone} />
                  </td>
                  <td>
                    <span className={`tag dir-${message.direction}`}>
                      {message.direction === 'in' ? 'in ←' : 'out →'}
                    </span>
                  </td>
                  {/* `pre-wrap`, not a paragraph. A WhatsApp message's own line breaks
                      are part of what was said, and a question with its options on
                      separate lines is unreadable reflowed into prose. */}
                  <td className="cell-prose">
                    <span className="wrapped">{message.text}</span>
                  </td>
                  <td>
                    <span className="stack">
                      {message.purpose && <span className="sub">{message.purpose}</span>}
                      {message.itemId && <Id value={message.itemId} />}
                      {message.outcome && (
                        <span className={`tag outcome-${message.outcome}`}>{message.outcome}</span>
                      )}
                      {!message.purpose && !message.itemId && !message.outcome && <Nil />}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-actions">
        {loading && <span className="sub">loading…</span>}
        {!done && !loading && (
          <>
            <button type="button" className="admin-button quiet" onClick={loadMore}>
              Load more
            </button>
            <button type="button" className="admin-button quiet" onClick={loadAll}>
              Load all
            </button>
          </>
        )}
      </div>
    </section>
  );
}

export default function Learner() {
  const { userId } = useParams<{ userId: string }>();
  const [detail, setDetail] = useState<AdminLearnerDetail>();
  const [error, setError] = useState<ApiError>();
  const [phone, setPhone] = useState<string>();

  useEffect(() => {
    if (!userId) return;
    let stale = false;
    setDetail(undefined);
    setError(undefined);
    setPhone(undefined);
    void getLearner(userId).then((result) => {
      // Switching learners while a request is in flight would otherwise land the
      // previous learner's data — and their revealed phone number — on this page.
      if (stale) return;
      if (isError(result)) setError(result);
      else setDetail(result);
    });
    return () => {
      stale = true;
    };
  }, [userId]);

  if (!userId) return null;
  if (error) return <Failed error={error} what={`Learner ${userId}`} />;
  if (!detail) return <Waiting what="learner" />;

  const learner = detail.learner;
  const objectives = detail.objectives ?? [];

  return (
    <>
      <nav className="crumbs">
        <Link className="admin-link" to="/admin">
          learners
        </Link>
        <span aria-hidden="true">/</span>
        <span>{learner.name || learner.userId}</span>
      </nav>

      <WhyNothing learner={learner} state={detail.sweepState} />

      <section className="admin-panel">
        <header className="admin-head">
          <h1>{learner.name || '(no name)'}</h1>
          <p className="sub">
            <Id value={learner.userId} /> · joined {ago(learner.createdAt)}
          </p>
        </header>

        <dl className="facts">
          <div>
            <dt>Phone</dt>
            <dd>
              {phone ? (
                <span className="stack">
                  <Id value={phone} />
                  <button type="button" className="admin-link" onClick={() => setPhone(undefined)}>
                    hide
                  </button>
                </span>
              ) : (
                <span className="stack">
                  <Id value={learner.phoneMasked} />
                  <button
                    type="button"
                    className="admin-link"
                    onClick={async () => {
                      const result = await revealPhone(learner.userId);
                      if (!isError(result)) setPhone(result.phone);
                    }}
                  >
                    reveal
                  </button>
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt>Locale</dt>
            <dd>{learner.locale}</dd>
          </div>
          <div>
            <dt>Timezone</dt>
            <dd>
              {learner.timezone || '—'} · {inZone(new Date().toISOString(), learner.timezone)} local
            </dd>
          </div>
          <div>
            <dt>Window</dt>
            <dd>
              {learner.window
                ? `${learner.window.reachFrom}–${learner.window.reachUntil}, ${learner.window.dailyLimit} questions/day${learner.window.explanations ? ', explanations on' : ''}`
                : 'unset'}
            </dd>
          </div>
          <div>
            <dt>Last inbound</dt>
            <dd title={learner.lastInboundAt ?? undefined}>{ago(learner.lastInboundAt)}</dd>
          </div>
          <div>
            <dt>Last outbound</dt>
            <dd title={learner.lastOutboundAt ?? undefined}>{ago(learner.lastOutboundAt)}</dd>
          </div>
          <div>
            <dt>Reminders</dt>
            <dd>{learner.reminders}</dd>
          </div>
          <div>
            <dt>Usage (whole account)</dt>
            <dd>{summariseUsage(learner.usage)}</dd>
          </div>
        </dl>

        <div className="admin-actions">
          <ConfirmedAction
            label="Force a sweep"
            consequence="If anything is due and they are inside their hours, this puts a question on their phone now instead of within five minutes."
            run={() => sweep(learner.userId)}
          />
          <a
            className="admin-button quiet"
            href={logsRecent(LOG_GROUPS.sweep, 2)}
            target="_blank"
            rel="noreferrer"
          >
            Sweep log, last 2h
          </a>
          <a
            className="admin-button quiet"
            href={logsRecent(LOG_GROUPS.inbound, 2)}
            target="_blank"
            rel="noreferrer"
          >
            Inbound log, last 2h
          </a>
        </div>
      </section>

      <section className="admin-panel">
        <header className="admin-head">
          <h2>Objectives</h2>
          <p className="sub">{objectives.length} in total</p>
        </header>
        {objectives.length === 0 ? (
          <p className="admin-empty">No objectives.</p>
        ) : (
          <div className="admin-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Closed</th>
                  <th>objectiveId</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {objectives.map((objective) => (
                  <tr key={objective.objectiveId}>
                    <td>
                      <Link
                        className="admin-link strong"
                        to={`/admin/objective/${objective.objectiveId}`}
                      >
                        {localTitle(objective.title)}
                      </Link>
                    </td>
                    <td>
                      <span className={`tag status-${objective.status}`}>{objective.status}</span>
                    </td>
                    <td className="num" title={objective.createdAt}>
                      {objective.createdAt ? ago(objective.createdAt) : <Nil />}
                    </td>
                    <td className="num" title={objective.closedAt ?? undefined}>
                      {objective.closedAt ? ago(objective.closedAt) : <Nil what="open" />}
                    </td>
                    <td>
                      <Id value={objective.objectiveId} />
                    </td>
                    <td>
                      <Link
                        className="admin-link"
                        to={`/admin/objective/${objective.objectiveId}/items`}
                      >
                        items →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Above the timelines, because "this learner says we got something wrong" outranks
          "here is everything we sent them" — and because a dispute is the only row on this
          page that somebody outside the system took the trouble to write. */}
      <DisputesPanel
        userId={learner.userId}
        timezone={learner.timezone}
        heading="What they say we got wrong"
      />
      <Sends userId={learner.userId} timezone={learner.timezone} />
      <Messages userId={learner.userId} timezone={learner.timezone} />
    </>
  );
}
